import type { EatalyBreadcrumbItem } from '@/components/eataly-breadcrumbs';
import type { ShopperProducts } from '@/scapi';
import { mapCategoryTreeToUrls } from '@/lib/category-link-target';
import type { SCAPIShopperProductsSchemas } from 'src/types';

/** Input segment structure from APIs (e.g., category `parentCategoryTree`). */
export type BreadcrumbSegmentInput = {
    id?: string;
    label?: string;
    name?: string;
    /** Required for linked intermediate segments; omitted on `tail` (never receives `href`). */
    url?: string;
};

export type BuildEatalyBreadcrumbItemsParams = {
    /** Intermediate trail segments (e.g., parent categories) */
    segments: BreadcrumbSegmentInput[];
    /** IDs that should be rendered as plain text instead of links (e.g., current PLP category) */
    nonLinkIds?: Set<string> | string[] | null;
    /** Final crumb (e.g., product name). Never receives an `href`. */
    tail?: BreadcrumbSegmentInput | null;
};

/**
 * [Internal] Normalizes the nonLinkIds input into a Set.
 * Using a Set ensures O(1) lookup performance when checking if a segment should be a link.
 */
function toNonLinkIdSet(nonLinkIds?: Set<string> | string[] | null): Set<string> {
    if (nonLinkIds == null) {
        return new Set();
    }
    if (nonLinkIds instanceof Set) {
        return nonLinkIds;
    }
    return new Set(nonLinkIds);
}

/**
 * [Internal] Extracts and cleans the display string from a segment.
 * Priority: label > name.
 * Handles null/undefined values and trims whitespace to prevent UI alignment issues.
 */
function normalizeLabel(seg: BreadcrumbSegmentInput): string {
    const raw = seg.label ?? seg.name;
    if (raw == null) {
        return '';
    }
    return String(raw).trim();
}

/**
 * Transforms generic API segments into `EatalyBreadcrumbItem[]` for the Breadcrumbs component.
 */
export function buildEatalyBreadcrumbItems({
    segments,
    nonLinkIds,
    tail,
}: BuildEatalyBreadcrumbItemsParams): EatalyBreadcrumbItem[] {
    const noLink = toNonLinkIdSet(nonLinkIds);
    const out: EatalyBreadcrumbItem[] = [];

    for (let index = 0; index < segments.length; index++) {
        const seg = segments[index];
        const label = normalizeLabel(seg);

        if (!label) {
            continue;
        }

        const rawId = seg.id;
        const hasRealId = rawId != null && String(rawId).trim().length > 0;
        const id = hasRealId ? String(rawId).trim() : `segment-${index}`;

        let href: string | undefined;
        if (hasRealId) {
            const idStr = String(rawId).trim();
            if (!noLink.has(idStr) && seg.url !== undefined) {
                href = seg.url;
            }
        }

        if (href !== undefined) {
            out.push({ id, label, href });
        } else {
            out.push({ id, label });
        }
    }

    if (tail) {
        const normalizedTail = normalizeLabel(tail);
        if (normalizedTail) {
            const tailIndex = out.length;
            const rawTid = tail.id;
            const hasTid = rawTid != null && String(rawTid).trim().length > 0;
            const id = hasTid ? String(rawTid).trim() : `tail-${tailIndex}`;

            out.push({ id, label: normalizedTail });
        }
    }

    return out;
}

/**
 * Produces the PLP trail. Call from `_app.c.$.tsx` and assign the result to `breadcrumbItems`.
 */
export function buildEatalyCategoryBreadcrumbItems(
    category: ShopperProducts.schemas['Category']
): EatalyBreadcrumbItem[] {
    const segments = mapCategoryTreeToUrls(category);

    return buildEatalyBreadcrumbItems({
        segments,
        nonLinkIds: [String(category.id)],
    });
}

/**
 * Produces the PDP trail. Call from the product route `loader` and expose as `breadcrumbItems`.
 */
export function buildEatalyProductBreadcrumbItems(
    product: SCAPIShopperProductsSchemas['Product'],
    category?: SCAPIShopperProductsSchemas['Category']
): EatalyBreadcrumbItem[] {
    return buildEatalyBreadcrumbItems({
        segments: category ? mapCategoryTreeToUrls(category) : [],
        tail: { id: product.id, name: product.name },
    });
}
